import React, { useMemo, useState } from "react";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import type {
  CatalogApplicationRecord,
  CatalogJobListItem,
} from "@tabbio/types";
import { WebDialog } from "../common/WebDialog.web";
import { MaterialSymbol } from "../icons/MaterialSymbol";
import {
  useCatalogApplyHistory,
  useCatalogAutoApplyOverview,
  useCatalogSavedJobs,
  useUpdateCatalogAutoApplyRule,
} from "../../hooks/useJobsApi";
import { useTailoredCVs } from "../../hooks/useTailoredCvApi";
import { useUaePassStatus } from "../../hooks/useUaePassStatus";
import { useToast } from "../../hooks/useToast";
import {
  createMasterCvListItem,
  defaultCvIdForListItem,
  isMainCvListItem,
  type CvListItem,
} from "../../lib/cv-main-default";
import { hasCvHandoff } from "../../lib/cv-handoff-ui";
import { buildJobPreferencesRoute } from "../../lib/job-preferences-route";
import type { TailoredCV } from "../../lib/tailored-cv-api";
import {
  formatRelativeDate,
  getCatalogJobDisplayDate,
} from "../../lib/jobs-format";
import { UaePassVerifiedBadge } from "../uae-pass/UaePassVerifiedBadge";
import { UaePassVerificationContent } from "../uae-pass/UaePassVerificationContent";

export type JobsQuickActionPanel =
  | "preferences"
  | "primaryCv"
  | "alerts"
  | "verification"
  | "myJobs"
  | null;

type MyJobsTab = "saved" | "applied";

function getUniqueJobsById<T extends CatalogJobListItem>(jobs: readonly T[]) {
  const seen = new Set<string>();
  return jobs.filter((job) => {
    if (seen.has(job.id)) {
      return false;
    }
    seen.add(job.id);
    return true;
  });
}

interface JobsQuickActionSurfaceProps {
  panel: JobsQuickActionPanel;
  onPanelChange: (panel: JobsQuickActionPanel) => void;
  onClose: () => void;
  onOpenJob: (jobId: string) => void;
}

function getRule(
  overview: ReturnType<typeof useCatalogAutoApplyOverview>["data"],
  key: string,
) {
  return overview?.rules.find((rule) => rule.key === key) ?? null;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        margin: "16px 0 8px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: "var(--text-tertiary)",
      }}
    >
      {children}
    </div>
  );
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        overflow: "hidden",
        borderRadius: 20,
        border: "1px solid var(--input-border)",
        background: "var(--tabbio-surface-secondary, rgba(0,0,0,0.035))",
      }}
    >
      {children}
    </div>
  );
}

function Row({
  icon,
  title,
  subtitle,
  value,
  onClick,
  trailing,
  isLast,
}: {
  icon: string | React.ReactNode;
  title: string;
  subtitle?: string | null;
  value?: string | null;
  onClick?: () => void;
  trailing?: React.ReactNode;
  isLast?: boolean;
}) {
  const baseStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    minHeight: 64,
    padding: "12px 14px",
    border: 0,
    borderBottom: isLast ? 0 : "1px solid var(--input-border)",
    background: "transparent",
    color: "var(--text-primary)",
    textAlign: "start",
    cursor: onClick ? "pointer" : "default",
    fontFamily: "inherit",
  };

  const children = (
    <>
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          background: "transparent",
          color: "var(--text-secondary)",
          flexShrink: 0,
        }}
      >
        {typeof icon === "string" ? (
          <MaterialSymbol name={icon} size={19} />
        ) : (
          icon
        )}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: "block",
            fontSize: 14,
            fontWeight: 650,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </span>
        {subtitle ? (
          <span
            style={{
              display: "block",
              marginTop: 2,
              fontSize: 12,
              lineHeight: 1.4,
              color: "var(--text-tertiary)",
            }}
          >
            {subtitle}
          </span>
        ) : null}
      </span>
      {trailing ??
        (value ? (
          <span
            style={{
              maxWidth: 130,
              color: "var(--text-tertiary)",
              fontSize: 12,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </span>
        ) : onClick ? (
          <MaterialSymbol
            name="chevron_right"
            size={18}
            style={{ color: "var(--text-tertiary)" }}
          />
        ) : null)}
    </>
  );

  if (!onClick) {
    return <div style={baseStyle}>{children}</div>;
  }

  return (
    <button type="button" onClick={onClick} style={baseStyle}>
      {children}
    </button>
  );
}

function ChoiceRow({
  title,
  subtitle,
  selected,
  onClick,
  isLast,
}: {
  title: string;
  subtitle?: string | null;
  selected: boolean;
  onClick: () => void;
  isLast?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        width: "100%",
        minHeight: 58,
        padding: "12px 14px",
        border: 0,
        borderBottom: isLast ? 0 : "1px solid var(--input-border)",
        background: "transparent",
        color: "var(--text-primary)",
        textAlign: "start",
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 14, fontWeight: 650 }}>
          {title}
        </span>
        {subtitle ? (
          <span
            style={{
              display: "block",
              marginTop: 2,
              fontSize: 12,
              color: "var(--text-tertiary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {subtitle}
          </span>
        ) : null}
      </span>
      <span
        aria-hidden
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 22,
          height: 22,
          borderRadius: "50%",
          border: `1.5px solid ${selected ? "var(--text-primary)" : "var(--input-border)"}`,
          background: selected ? "var(--text-primary)" : "transparent",
          color: "var(--bg)",
          flexShrink: 0,
        }}
      >
        {selected ? <MaterialSymbol name="check" size={14} /> : null}
      </span>
    </button>
  );
}

function Toggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onChange(!checked);
      }}
      style={{
        width: 46,
        height: 28,
        border: 0,
        borderRadius: 999,
        padding: 3,
        background: checked ? "var(--tabbio-button-primary-bg)" : "var(--input-border)",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.55 : 1,
        transition: "background-color 150ms",
      }}
    >
      <span
        style={{
          display: "block",
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: "#fff",
          transform: checked ? "translateX(18px)" : "translateX(0)",
          transition: "transform 150ms",
        }}
      />
    </button>
  );
}

function PreferencesPanel({
  onPanelChange,
  onOpenJobPreferences,
}: {
  onPanelChange: (panel: JobsQuickActionPanel) => void;
  onOpenJobPreferences: () => void;
}) {
  const { t } = useTranslation("seeker");
  const status = useUaePassStatus(true);
  const overview = useCatalogAutoApplyOverview(true);
  const defaultCvId =
    getRule(overview.data, "auto-apply")?.settings.defaultCvId ?? null;
  const jobAlertRule = getRule(overview.data, "job-alerts");

  return (
    <>
      <SectionLabel>{t("jobs.preferences.myInterests")}</SectionLabel>
      <SectionCard>
        <Row
          icon="person"
          title={t("jobs.preferences.openToWorkTitle")}
          subtitle={t("jobs.preferences.openToWorkSubtitle")}
          onClick={onOpenJobPreferences}
        />
        <Row
          icon="notifications"
          title={t("jobs.preferences.jobAlertsTitle")}
          subtitle={
            jobAlertRule?.enabled
              ? t("jobs.preferences.jobAlertsEnabled")
              : t("jobs.preferences.jobAlertsDisabled")
          }
          onClick={() => onPanelChange("alerts")}
          isLast
        />
      </SectionCard>

      <SectionLabel>{t("jobs.preferences.primaryCvSection")}</SectionLabel>
      <SectionCard>
        <Row
          icon="description"
          title={t("jobs.preferences.primaryCvTitle")}
          subtitle={t("jobs.preferences.primaryCvSubtitle")}
          value={
            defaultCvId
              ? t("jobs.preferences.customCv")
              : t("cv.list.masterTitle")
          }
          onClick={() => onPanelChange("primaryCv")}
          isLast
        />
      </SectionCard>

      <SectionLabel>{t("jobs.preferences.verificationSection")}</SectionLabel>
      <SectionCard>
        <Row
          icon={<UaePassVerifiedBadge size={19} color="currentColor" />}
          title={t("jobs.preferences.verificationTitle")}
          subtitle={
            status.data?.isLinked
              ? t("jobs.preferences.verificationLinked")
              : t("jobs.preferences.verificationUnlinked")
          }
          value={status.data?.isLinked ? t("jobs.preferences.verified") : null}
          onClick={() => onPanelChange("verification")}
          isLast
        />
      </SectionCard>
    </>
  );
}

function PrimaryCvPanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation("seeker");
  const toast = useToast();
  const { data: cvs, isLoading } = useTailoredCVs(true);
  const { data: overview } = useCatalogAutoApplyOverview(true);
  const updateDefaultCv = useUpdateCatalogAutoApplyRule();
  const masterCv = useMemo(
    () => createMasterCvListItem(t("cv.list.masterTitle")),
    [t],
  );
  const defaultCvId =
    getRule(overview, "auto-apply")?.settings.defaultCvId ?? null;
  const cvOptions = useMemo<CvListItem[]>(
    () => [
      masterCv,
      ...((cvs ?? []) as CvListItem[]).filter(
        (cv) => !hasCvHandoff(cv as TailoredCV),
      ),
    ],
    [cvs, masterCv],
  );

  const selectCv = async (cv: CvListItem) => {
    const nextDefaultCvId = defaultCvIdForListItem(cv);
    if (nextDefaultCvId === defaultCvId || updateDefaultCv.isPending) return;
    try {
      await updateDefaultCv.mutateAsync({
        ruleKey: "auto-apply",
        input: { settings: { defaultCvId: nextDefaultCvId } },
      });
      toast.success(t("jobs.primaryCv.saved"));
      onClose();
    } catch (error) {
      toast.error(
        t("jobs.primaryCv.saveFailed"),
        error instanceof Error ? error.message : undefined,
      );
    }
  };

  if (isLoading) {
    return (
      <div
        style={{
          padding: 32,
          textAlign: "center",
          color: "var(--text-tertiary)",
        }}
      >
        {t("common:loading")}
      </div>
    );
  }

  return (
    <SectionCard>
      {cvOptions.map((cv, index) => (
        <ChoiceRow
          key={cv.id}
          title={cv.title}
          subtitle={
            cv.id === "master"
              ? t("cv.list.masterDescription")
              : formatRelativeDate(cv.updatedAt, i18n.language)
          }
          selected={isMainCvListItem(cv, defaultCvId)}
          onClick={() => selectCv(cv)}
          isLast={index === cvOptions.length - 1}
        />
      ))}
    </SectionCard>
  );
}

function AlertsPanel({
  onEdit,
}: {
  onEdit: () => void;
}) {
  const { t } = useTranslation("seeker");
  const { data: overview, isLoading } = useCatalogAutoApplyOverview(true);
  const updateRule = useUpdateCatalogAutoApplyRule();
  const rule = getRule(overview, "job-alerts");
  const summaryParts = [
    rule?.settings.locations?.[0],
    rule?.settings.workModes?.[0],
    rule?.frequency
      ? t(
          `jobs.alerts.frequency.${rule.frequency as "daily" | "weekly"}` as never,
        )
      : null,
  ].filter(Boolean);

  if (isLoading) {
    return (
      <div
        style={{
          padding: 32,
          textAlign: "center",
          color: "var(--text-tertiary)",
        }}
      >
        {t("common:loading")}
      </div>
    );
  }

  return (
    <>
      <SectionCard>
        <Row
          icon="notifications"
          title={t("jobs.alerts.defaultRuleTitle")}
          subtitle={
            summaryParts.length
              ? summaryParts.join(" · ")
              : t("jobs.alerts.defaultRuleSubtitle")
          }
          value={
            rule?.enabled ? t("jobs.alerts.enabled") : t("jobs.alerts.disabled")
          }
          onClick={onEdit}
          isLast
        />
      </SectionCard>

      <div style={{ height: 12 }} />

      <SectionCard>
        <Row
          icon="lightbulb"
          title={t("jobs.alerts.topPicksTitle")}
          subtitle={t("jobs.alerts.topPicksSubtitle")}
          trailing={
            <Toggle
              checked={rule?.enabled ?? false}
              disabled={updateRule.isPending || !rule}
              onChange={(enabled) =>
                updateRule.mutate({
                  ruleKey: "job-alerts",
                  input: { enabled },
                })
              }
            />
          }
          isLast
        />
      </SectionCard>
    </>
  );
}

function VerificationPanel({ onClose }: { onClose: () => void }) {
  return <UaePassVerificationContent onClose={onClose} />;
}

function MyJobsPanel({
  onClose,
  onOpenJob,
}: {
  onClose: () => void;
  onOpenJob: (jobId: string) => void;
}) {
  const { t, i18n } = useTranslation("seeker");
  const [tab, setTab] = useState<MyJobsTab>("saved");
  const saved = useCatalogSavedJobs(50, true);
  const applied = useCatalogApplyHistory(50, true);
  const savedItems = useMemo(
    () => getUniqueJobsById(saved.data ?? []),
    [saved.data],
  );
  const items = tab === "saved" ? savedItems : (applied.data ?? []);
  const isLoading = tab === "saved" ? saved.isLoading : applied.isLoading;

  const openJob = (jobId: string) => {
    onClose();
    onOpenJob(jobId);
  };

  return (
    <>
      <div
        role="tablist"
        style={{
          display: "flex",
          gap: 4,
          padding: 4,
          borderRadius: 999,
          background: "rgba(0,0,0,0.04)",
          marginBottom: 14,
        }}
      >
        {(["saved", "applied"] as MyJobsTab[]).map((nextTab) => {
          const active = tab === nextTab;
          const count =
            nextTab === "saved"
              ? (saved.data?.length ?? 0)
              : (applied.data?.length ?? 0);
          return (
            <button
              key={nextTab}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(nextTab)}
              style={{
                flex: 1,
                minHeight: 36,
                border: 0,
                borderRadius: 999,
                background: active ? "var(--text-primary)" : "transparent",
                color: active ? "var(--bg)" : "var(--text-secondary)",
                cursor: active ? "default" : "pointer",
                fontSize: 13,
                fontWeight: 650,
                fontFamily: "inherit",
              }}
            >
              {t(`jobs.myJobs.${nextTab}`, { count })}
            </button>
          );
        })}
      </div>

      <SectionCard>
        {isLoading ? (
          <div
            style={{
              padding: 32,
              textAlign: "center",
              color: "var(--text-tertiary)",
            }}
          >
            {t("common:loading")}
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center" }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "var(--text-primary)",
              }}
            >
              {tab === "saved"
                ? t("jobs.myJobs.emptySaved")
                : t("jobs.myJobs.emptyApplied")}
            </div>
            <div
              style={{
                marginTop: 6,
                fontSize: 13,
                color: "var(--text-tertiary)",
              }}
            >
              {tab === "saved"
                ? t("jobs.myJobs.emptySavedSubtitle")
                : t("jobs.myJobs.emptyAppliedSubtitle")}
            </div>
          </div>
        ) : (
          (items as (CatalogJobListItem | CatalogApplicationRecord)[]).map(
            (item, index) => {
              const job = "catalogJobId" in item ? item.job : item;
              const jobId =
                "catalogJobId" in item ? item.catalogJobId : item.id;
              return (
                <Row
                  key={item.id}
                  icon="business_center"
                  title={job.title}
                  subtitle={[job.company.name, job.location]
                    .filter(Boolean)
                    .join(" · ")}
                  value={
                    "catalogJobId" in item
                      ? t(`jobs.status.${item.status}` as never)
                      : formatRelativeDate(
                          getCatalogJobDisplayDate(job),
                          i18n.language,
                        )
                  }
                  onClick={() => openJob(jobId)}
                  isLast={index === items.length - 1}
                />
              );
            },
          )
        )}
      </SectionCard>
    </>
  );
}

function getDialogTitle(
  panel: Exclude<JobsQuickActionPanel, null>,
  t: ReturnType<typeof useTranslation>["t"],
) {
  switch (panel) {
    case "primaryCv":
      return t("jobs.primaryCv.title");
    case "alerts":
      return t("jobs.alerts.title");
    case "verification":
      return t("jobs.uaePass.title");
    case "myJobs":
      return t("jobs.myJobs.title");
    case "preferences":
    default:
      return t("jobs.preferences.title");
  }
}

function getBackPanel(
  panel: Exclude<JobsQuickActionPanel, null>,
): JobsQuickActionPanel {
  if (panel === "primaryCv" || panel === "verification") return "preferences";
  return null;
}

export function JobsQuickActionSurface({
  panel,
  onPanelChange,
  onClose,
  onOpenJob,
}: JobsQuickActionSurfaceProps) {
  const router = useRouter();
  const { t } = useTranslation("seeker");
  if (!panel) return null;

  const backPanel = getBackPanel(panel);

  return (
    <WebDialog
      visible
      title={getDialogTitle(panel, t)}
      subtitle={
        panel === "primaryCv" ? t("jobs.primaryCv.subtitle") : undefined
      }
      onClose={onClose}
      onBack={backPanel ? () => onPanelChange(backPanel) : undefined}
      maxWidth={panel === "myJobs" ? 560 : 500}
    >
      {panel === "preferences" ? (
        <PreferencesPanel
          onPanelChange={onPanelChange}
          onOpenJobPreferences={() => {
            onClose();
            router.push(buildJobPreferencesRoute());
          }}
        />
      ) : panel === "primaryCv" ? (
        <PrimaryCvPanel onClose={onClose} />
      ) : panel === "alerts" ? (
        <AlertsPanel
          onEdit={() => {
            onClose();
            router.push("/settings/job-alerts" as never);
          }}
        />
      ) : panel === "verification" ? (
        <VerificationPanel onClose={onClose} />
      ) : (
        <MyJobsPanel onClose={onClose} onOpenJob={onOpenJob} />
      )}
    </WebDialog>
  );
}
